import React, { FunctionComponent, useContext } from 'react';

import fetchJSON from '@api/api_helper';
import { InputType } from '@components/library/inputs/Input.props';
import InputContainer from '@components/library/input_container/InputContainer';
import AssetNameInput from '@components/asset/modal/tabs/edit/side_bar/assetNameInput';
import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';
import { initializeTrumbowyg } from '@components/asset/modal/tabs/edit/main_pane/detailsSection';
import {
  ActionTypes,
  AssetDetailsDispatch,
  ReactTag,
  ValidationErrors,
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { ExternalMediumData } from '@/api/v4/assets/assetTypes';

interface ExternalMediumSideBarPropsScopedContext {
  state: {
    editState: {
      assetDescription: string;
      assetName: string;
      externalMediumData: ExternalMediumData;
      tags: ReactTag[];
    };
    errorState: ValidationErrors[];
  };
  dispatch: AssetDetailsDispatch;
}

interface EmbedlyResponse {
  description: string;
  type: string;
  provider_name?: string;
  title?: string;
}

const emptyExternalMediumData: ExternalMediumData = {
  url: ''
};

const ExternalMediumSideBar: FunctionComponent<{updateFetchControllers: () => void}> = ({ updateFetchControllers }) => {
  const { state, dispatch }: ExternalMediumSideBarPropsScopedContext = useContext(assetModalContext);
  const { assetName, assetDescription, tags } = state.editState;
  const externalMediumData = state.editState.externalMediumData || emptyExternalMediumData;
  const hasInputError = state.errorState.includes(ValidationErrors.MissingMediaUrl);

  const getEmbedlyInfoAsync = async (): Promise<void> => {
    // This runs on every onBlur event if one of the things it could fill in
    // (asset name, asset description, or tags) is empty.
    // This runs on both the new asset form and the update form
    const embedlyUrl = `https://api.embed.ly/1/oembed?key=${BFG.EMBEDLY_API_KEY}&maxheight=385&frame=true&secure=true&url=${encodeURIComponent(externalMediumData.url)}`;
    if (!assetName || !assetDescription || tags.length === 0) {
      const embedlyResponse: EmbedlyResponse = await fetchJSON(embedlyUrl, {}, updateFetchControllers);
      dispatch({
        type: ActionTypes.UpdateAsset,
        payload: {
          assetName: assetName || embedlyResponse.title || "",
          assetDescription: assetDescription || embedlyResponse.description,
          tags: tags.length === 0
            ? [
              { name: embedlyResponse.type, id: embedlyResponse.type, auto_generated: false },
              { name: embedlyResponse.provider_name, id: embedlyResponse.provider_name, auto_generated: false }
            ]
            : tags
        }
      });
      // re-initialize wysiwyg editor with new asset description
      initializeTrumbowyg(assetDescription || embedlyResponse.description, dispatch);
    }
  };

  const getEmbedlyInfo = (): void => {
    getEmbedlyInfoAsync();
  };

  return (
    <div className="external-medium-sidebar edit-tab-sidebar one-third bf-scroll-element">
      <AssetNameInput assetNameCopy={t("Name")} />
      <InputContainer
        submitAttempted={hasInputError}
        attributes={{
          name: 'media_url',
          onChange: (e: InputChangeEvent): void => dispatch({
            type: ActionTypes.UpdateAsset,
            payload: { externalMediumData: { url: e.target.value } }
          }),
          onBlur: (): void => getEmbedlyInfo(),
          type: InputType.Text,
          value: externalMediumData.url,
        }}
        input={{ value: externalMediumData.url, error: hasInputError ? t('Media URL required') : '' }}
        labelCopy="Media URL"
      />
    </div>
  );
};

export default ExternalMediumSideBar;
